<template>
  <div class="page-container">
    <MainTitle title="Vocabulary List" />
    <div class="app-content-container">
      <div class="content-wrapper">
        <div class="vocabulary-menu">
          <v-select
            v-model="selectedSubjectId"
            :items="subjectOptions"
            item-title="subject_name"
            item-value="id"
            label="Filter by Subject"
            variant="outlined"
            density="compact"
            hide-details
            class="subject-filter"
            :elevation="0"
          />
          <v-btn
            class="ns-btn-primary review-subject-btn"
            :elevation="0"
            @click="isPerSubject = true; goToFlashCards()"
          >
            Review by Subject
          </v-btn>
          <v-btn
            data-test="review-btn"
            class="ns-btn-primary review-all-btn"
            :elevation="0"
            @click="goToFlashCards"
          >
            Review all
          </v-btn>
        </div>
        <div class="vocabularies">
          <div 
            v-for="(voc, index) in filteredVocabularyList" 
            :key="voc" 
            class="vocabulary-list"
            :data-test="`vocabulary-item-${index}`"
          >
            <VocabularyCard
              :card="voc"
              @select="openCard"
            />
          </div>
        </div>
      </div>
    </div>

    <v-dialog
      v-model="dialogOpen"
      max-width="480"
    >
      <v-card
        v-if="selectedCard"
        class="ns-card vocab-detail-card"
        :elevation="0"
      >
        <div class="vocab-detail-header">
          <span class="jlpt-tag">{{ formattedSelectedJlpt }}</span>
          <v-btn
            class="ns-btn-secondary"
            icon
            :elevation="0"
            size="small"
            @click="dialogOpen = false"
          >
            <font-awesome-icon icon="xmark" />
          </v-btn>
        </div>
        <div class="vocab-detail-word">
          {{ selectedCard.word }}
        </div>
        <div
          v-if="selectedCard.reading"
          class="vocab-detail-reading"
        >
          ( {{ selectedCard.reading }} )
        </div>
        <hr class="vocab-detail-divider">
        <p class="vocab-detail-definition">
          {{ selectedCard.definition }}
        </p>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import VocabularyCard from "@/components/flashcards/VocabularyCard.vue";
  import MainTitle from "../title/MainTitle.vue";
  import {
    getWithAuthentication,
  } from "@/services/api";
  import {
    BASE_URL,
    DICTIONARY_VOCABULARY_ENDPOINT,
    LIBRARY_SUBJECT_ENDPOINT,
  } from "@/assets/constants/api_endpoints";

  export default {
    components: {
      VocabularyCard,
      MainTitle,
    },
    data() {
      return {
        vocabularyList: [],
        subjectList: [],
        selectedSubjectId: null,
        newVocabulary: "",
        isAddingNewVocabulary: false,
        isPerSubject: false,
        selectedCard: null,
        dialogOpen: false,
      };
    },
    computed: {
      formattedSelectedJlpt() {
        if (!this.selectedCard?.jlpt) return 'JLPT N1';
        const match = this.selectedCard.jlpt.match(/^jlpt-(n\d)$/i);
        return match ? `JLPT ${match[1].toUpperCase()}` : this.selectedCard.jlpt.toUpperCase();
      },
      filteredVocabularyList() {
        if (!this.selectedSubjectId) return this.vocabularyList;

        const selectedSubject = this.subjectList.find(
          (subject) => String(subject.id) === String(this.selectedSubjectId)
        );
        console.log("Selected Subject:", selectedSubject);
        if (selectedSubject?.is_sample) {
          return this.vocabularyList.filter(voc => voc.is_sample);
        }
        
        return this.vocabularyList.filter(
          (voc) => String(voc.subject) === String(this.selectedSubjectId)
        );
      },
      subjectOptions() {
        return [
          { id: null, subject_name: 'All Subjects' },
          ...this.subjectList,
        ];
      },
    },
    async mounted() {
      await Promise.all([this.getVocabularyList(), this.getSubjectList()]);
      this.$store.state.vocabularyList = this.vocabularyList;
    },
    methods: {
      openAddVocabularyPopover(event) {
        this.$refs.popover.toggle(event);
      },
      async getVocabularyList() {
        try {
          const response = await getWithAuthentication(
            BASE_URL,
            DICTIONARY_VOCABULARY_ENDPOINT
          );
          if (response && response.status === 200) {
            this.vocabularyList = response.data.filter(
              (item) => item.is_deleted == false
            );
          }
        } catch (error) {
          this.$router.push({ name: "LoginForm", params: { loggedOut: true } });
        }
      },
      async getSubjectList() {
        try {
          const response = await getWithAuthentication(
            BASE_URL,
            LIBRARY_SUBJECT_ENDPOINT
          );
          if (response && response.status === 200) {
            this.subjectList = response.data;
          }
        } catch (error) {
          // subjects are optional for filtering; silently ignore
        }
      },
      showInfoNotification(status, message) {
        this.$toast.add({ severity: status, summary: message, life: 3000 });
      },
      goToFlashCards() {
        if (this.isPerSubject) {
          this.$router.push({ name: "VocabularyReview", params: { subjectId: this.selectedSubjectId } });
        } else {
          this.$router.push({ name: "VocabularyReview"});
        }
      },
      openCard(card) {
        this.selectedCard = card;
        this.dialogOpen = true;
      }
    },
  };
</script>

<style scoped>
  @media (max-width: 599px) {
      .review-all-btn {
          margin-left: 0px !important;
      }
      .vocabulary-menu {
        margin-left: 0px !important;
      }
  }

  .review-all-btn {
      margin-left: 20px;
      margin-top: 20px; 
      width: 186px;
  }

  .review-subject-btn {
    margin-top: 20px; 
    width: 186px;
  }

  .content-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .vocabularies {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--space-4);
    width: 100%;
  }

  .vocabulary-list {
    width: 250px;
    display: flex;
    align-items: center;
  }

  .vocabulary-menu {
    width: 100%;
    display: flow;
    align-items: center;
    justify-content: space-between;
    margin-top: var(--space-5);
    margin-bottom: var(--space-4);
    padding: 0 var(--space-5);
    margin-left: 40px;
  }

  .subject-filter {
    max-width: 280px;
  }

  .vocab-detail-card {
    padding: var(--space-6) !important;
  }

  .vocab-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--space-4);
  }

  .jlpt-tag {
    background-color: var(--color-warning-light);
    color: var(--color-warning);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-bold);
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-sm);
  }

  .vocab-detail-word {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    text-align: center;
    margin-bottom: var(--space-2);
  }

  .vocab-detail-reading {
    font-size: var(--font-size-md);
    color: var(--color-text-secondary);
    text-align: center;
    margin-bottom: var(--space-4);
  }

  .vocab-detail-divider {
    border: none;
    border-top: 1px solid rgba(27,30,100,0.10);
    margin: var(--space-4) 0;
  }

  .vocab-detail-definition {
    font-size: var(--font-size-base);
    color: var(--color-text-primary);
    line-height: var(--line-height-relaxed);
  }
</style>
